import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Formik } from "formik";
import React, { useState } from "react";
import OtpInput from "react-otp-input";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { useRecaptcha } from "../../../contexts/RecaptchaContext.jsx";
import AuthWrapper from "../../../components/AuthWrapper/index.jsx";
import EmailVerificationPopup from "../../../components/EmailVerificationPopup/index.jsx";
import { Button, Input, TextView } from "../../../components/index.js";
import RadioButton from "../../../components/RadioButton/index.jsx";
import { phoneLookup } from "../../../services/api.js";
import {
  useForgotSendEmailOtp,
  useForgotSendPhoneOtp,
  useForgotVerifyEmailOtp,
  useForgotVerifyPhoneOtp,
  useResetPassword,
} from "../../../services/mutations.js";
import { PATH } from "../../../utils/config.js";
import { colors } from "../../../utils/theme.js";
import * as Styled from "../styles.js";
import { isEmpty } from "lodash";

// ReCAPTCHA Site Key - Get from environment variable
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
// ReCAPTCHA Feature Flag - Controls whether reCAPTCHA is enabled
const RECAPTCHA_ENABLED = process.env.REACT_APP_RECAPTCHA_ENABLED === "true";

const RECAPTCHA_ACTIONS = {
  FORGOT_PASSWORD_EMAIL: "forgot_password",
  FORGOT_PASSWORD_PHONE: "forgot_password_phone",
};

const getRecaptchaToken = async (executeRecaptcha, action) => {
  if (!RECAPTCHA_ENABLED) return null;
  if (!RECAPTCHA_SITE_KEY) {
    console.warn(
      "reCAPTCHA site key not configured. Proceeding without reCAPTCHA verification.",
    );
    return null;
  }
  if (!executeRecaptcha) {
    console.warn("reCAPTCHA not yet available");
    toast.error("reCAPTCHA not loaded. Please refresh the page.");
    return null;
  }
  try {
    return await executeRecaptcha(action);
  } catch (error) {
    console.error("reCAPTCHA execution failed:", error);
    toast.error("reCAPTCHA verification failed. Please refresh the page.");
    return null;
  }
};

const emailSchema = Yup.object().shape({
  email: Yup.string().email("Please Enter a Valid Email"),
});

const passwordSchema = Yup.object().shape({
  password: Yup.string()
    .required("Password is a Required field")
    .min(8, "Password must be at least 8 characters")
    .max(20, "Password must be at most 20 characters")
    .matches(
      /^(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*()_+|{}[\]:";'<>?,./])/,
      "Password must contain at least one uppercase letter, one numeric and one special character",
    ),
});

const ForgotPassword = () => {
  const [step, setStep] = useState(0);
  const [title, setTitle] = useState("Forgot Password");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [uid, setUid] = useState(null);
  const [token, setToken] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [method, setMethod] = useState("email");
  const [showEmailVerificationPopup, setShowEmailVerificationPopup] =
    useState(false);

  const nav = useNavigate();
  const { executeRecaptcha } = useRecaptcha();
  const emailOTP = useForgotSendEmailOtp();
  const phoneOTP = useForgotSendPhoneOtp();
  const verifyPhoneOTP = useForgotVerifyPhoneOtp();
  const verifyEmailOTP = useForgotVerifyEmailOtp();
  const resetPasssword = useResetPassword();

  const handleSubmitEmail = async (values, func) => {
    const { setErrors } = func;
    if (values.email == "")
      return setErrors({ email: "Please enter your email" });

    try {
      const res = await phoneLookup({
        email: values.email,
      });
      setEmail(values.email);

      if (res.data.data.phone) {
        setPhone(res.data.data.phone);
        setStep(1);
        setTitle("Reset Password");
      } else {
        await handleMethodSelection("email", values.email);
      }
    } catch (err) {
      if(err.response?.data?.status_code == 403){
        toast.error(err.response?.data?.message || "Something went wrong");
        return;
      }
      if (
        !isEmpty(err.response?.data?.data) &&
        !err.response?.data?.data?.is_email_verified
      ) {
        setStep(0);
        setShowEmailVerificationPopup(true);
        return;
      }
      toast.error(
        err?.response?.data?.message || err?.message || "Something went wrong",
      );
    }
  };

  const handleMethodSelection = async (
    selectedMethod,
    emailValue = email,
  ) => {
    setMethod(selectedMethod);
    setIsSubmitting(true);

    const action =
      selectedMethod === "email"
        ? RECAPTCHA_ACTIONS.FORGOT_PASSWORD_EMAIL
        : RECAPTCHA_ACTIONS.FORGOT_PASSWORD_PHONE;
    const recaptchaToken = await getRecaptchaToken(executeRecaptcha, action);
    if (RECAPTCHA_ENABLED && recaptchaToken === null) {
      setIsSubmitting(false);
      return;
    }

    if (selectedMethod === "email") {
      emailOTP.mutate(
        {
          email: emailValue,
          ...(RECAPTCHA_ENABLED && recaptchaToken
            ? { recaptcha_token: recaptchaToken }
            : {}),
        },
        {
          onSuccess: (response) => {
            setStep(2);
            setTitle("Enter OTP");
            toast.success(
              response?.data?.message || "OTP has been sent to your email!",
            );
            setIsSubmitting(false);
          },
          onError: (err) => {
            setIsSubmitting(false);
            if (
              err.response?.data?.data &&
              !err.response?.data?.data?.is_email_verified
            ) {
              setStep(0);
              setShowEmailVerificationPopup(true);
              return;
            }
            toast.error(
              err?.response?.data?.message ||
                err?.message ||
                "Something went wrong",
            );
          },
        },
      );
    } else if (selectedMethod === "phone") {
      phoneOTP.mutate(
        {
          email: emailValue,
          ...(RECAPTCHA_ENABLED && recaptchaToken
            ? { recaptcha_token: recaptchaToken }
            : {}),
        },
        {
          onSuccess: (response) => {
            setStep(2);
            setTitle("Enter OTP");
            toast.success(
              response?.data?.message ||
                "OTP has been sent to your phone number!",
            );
            setIsSubmitting(false);
          },
          onError: (err) => {
            setIsSubmitting(false);
            toast.error(
              err?.response?.data?.message ||
                err?.message ||
                "Something went wrong",
            );
          },
        },
      );
    }
  };

  const handleMethodSubmit = (values) => {
    if (!values.method) {
      toast.error("Please select a verification method");
      return;
    }
    handleMethodSelection(values.method);
  };

  const handleSubmitOtp = (values) => {
    setIsSubmitting(true);
    if (method == "email") {
      verifyEmailOTP.mutate(
        { email, otp: values.otp },
        {
          onSuccess: (response) => {
            const { uid, token } = response.data;
            setUid(uid);
            setToken(token);
            setStep(4);
            setTitle("Enter new password");
            setIsSubmitting(false);
            toast.success(response?.data?.message || "OTP has been verified!");
          },
          onError: (err) => {
            setIsSubmitting(false);
            toast.error(
              err?.response?.data?.message ||
                err?.message ||
                "Something went wrong",
            );
          },
        },
      );
    } else if (method == "phone") {
      verifyPhoneOTP.mutate(
        {
          email,
          otp: values.otp,
        },
        {
          onSuccess: (response) => {
            const { uid, token } = response.data;
            toast.success(response?.data?.message || "OTP has been verified!");
            setUid(uid);
            setToken(token);
            setStep(4);
            setTitle("Enter new password");
            setIsSubmitting(false);
          },
          onError: (err) => {
            setIsSubmitting(false);
            toast.error(
              err?.response?.data?.message ||
                err?.message ||
                "Something went wrong",
            );
          },
        },
      );
    }
  };

  const handleSubmitPassword = (values) => {
    setIsSubmitting(true);
    resetPasssword.mutate(
      {
        uid,
        token,
        new_password1: values?.password,
        new_password2: values?.confirmPassword,
      },
      {
        onSuccess: (response) => {
          setIsSubmitting(false);
          toast.success(
            response?.data?.detail || "Password reset successfully!",
          );
          nav(PATH.LOGIN);
        },
        onError: (error) => {
          setIsSubmitting(false);
          const error_msg =
            (error?.response?.data?.new_password1 &&
              error?.response?.data?.new_password1[0]) ||
            (error?.response?.data?.new_password2 &&
              error?.response?.data?.new_password2[0]) ||
            error?.message ||
            "Something went wrong";
          toast.error(error_msg);
        },
      },
    );
  };

  const renderEmailForm = () => {
    return (
      <>
        <TextView style={{ textAlign: "center", marginBottom: 20 }}>
          Please enter the email associated with your iYuga account.
        </TextView>
        <Formik
          validationSchema={emailSchema}
          initialValues={{ email: email }}
          onSubmit={handleSubmitEmail}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
          }) => (
            <form noValidate onSubmit={handleSubmit}>
              <Styled.InputWrapper>
                <Input
                  type="email"
                  name="email"
                  variant={"secondary"}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.email}
                  placeholder="Email"
                  containerStyle={{ marginBottom: 0 }}
                />
                <Styled.Error>
                  {errors.email && touched.email && <>{errors.email}</>}
                </Styled.Error>
              </Styled.InputWrapper>

              <Button
                style={{ marginBottom: 20, marginTop: 20 }}
                type="submit"
                isLoading={isSubmitting}
              >
                Continue
              </Button>

              <TextView
                onClick={() => nav("/login")}
                style={{
                  fontWeight: 700,
                  textAlign: "center",
                  marginTop: 10,
                  cursor: "pointer",
                }}
              >
                <ArrowBackIcon /> Back to Login
              </TextView>
            </form>
          )}
        </Formik>
      </>
    );
  };

  const renderMethodSelection = () => {
    const methodOptions = [
      {
        value: "email",
        label: `Send to my registered email (${email})`,
      },
      {
        value: "phone",
        label: `Send to my registered phone number (${phone})`,
      },
    ];

    return (
      <>
        <TextView style={{ textAlign: "center", marginBottom: 10 }}>
          How would you like to receive the code to reset your password?
        </TextView>

        <Formik
          initialValues={{ method: "email" }}
          onSubmit={handleMethodSubmit}
        >
          {({ values, handleSubmit, setFieldValue }) => (
            <form noValidate onSubmit={handleSubmit}>
              <RadioButton
                label=""
                options={methodOptions}
                selectedOption={values.method}
                onChange={(e) => setFieldValue("method", e.target.value)}
                orientation="vertical"
                style={{ marginBottom: 30 }}
              />

              <Button
                type="submit"
                isLoading={isSubmitting}
                disabled={!values.method}
                style={{ marginBottom: 20 }}
              >
                Next
              </Button>

              <TextView
                onClick={() => {
                  setStep(0);
                  setTitle("Forgot Password");
                }}
                style={{
                  fontWeight: 700,
                  textAlign: "center",
                  marginTop: 10,
                  cursor: "pointer",
                }}
              >
                <ArrowBackIcon /> Back
              </TextView>
            </form>
          )}
        </Formik>
      </>
    );
  };

  const renderOtpForm = () => {
    return (
      <>
        <TextView style={{ textAlign: "center", marginBottom: 20 }}>
          Please enter the OTP sent to {method === "email" ? email : phone}.
        </TextView>
        <Formik
          validationSchema={Yup.object().shape({
            otp: Yup.string().required("OTP is required"),
          })}
          initialValues={{ otp: "" }}
          onSubmit={handleSubmitOtp}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleSubmit,
            setFieldValue,
          }) => (
            <form noValidate onSubmit={handleSubmit}>
              <Styled.InputWrapper style={{ alignItems: "center" }}>
                <OtpInput
                  name="otp"
                  value={values.otp}
                  onChange={(value) => setFieldValue("otp", value)}
                  onBlur={handleBlur}
                  numInputs={6}
                  inputType="tel"
                  renderInput={(props) => <input {...props} pattern="[0-9]*" />}
                  inputStyle={{
                    width: 40,
                    height: 50,
                    borderRadius: 8,
                    color: "black",
                    border: `1px solid ${colors.PrimaryPurple}`,
                    textAlign: "center",
                  }}
                  renderSeparator={<span style={{ width: 10 }}></span>}
                />
                <Styled.Error>
                  {errors.otp && touched.otp && <>{errors.otp}</>}
                </Styled.Error>
              </Styled.InputWrapper>

              <Button
                disabled={values.otp.length !== 6}
                style={{ marginTop: 20, marginBottom: 20 }}
                type="submit"
                isLoading={isSubmitting}
              >
                Verify OTP
              </Button>

              <TextView style={{ textAlign: "center" }}>
                Did not receive a code?{" "}
                {method === "email" ? "Check your spam folder or " : ""}
                <span
                  onClick={() => {
                    if (method === "email") {
                      handleMethodSelection("email");
                    } else {
                      handleMethodSelection("phone");
                    }
                  }}
                  style={{
                    fontWeight: "bold",
                    color: colors.PrimaryPurple,
                    cursor: "pointer",
                  }}
                >
                  Click to resend!
                </span>
              </TextView>
              <TextView
                onClick={() => setStep(1)}
                style={{
                  fontWeight: 700,
                  textAlign: "center",
                  marginTop: 10,
                  cursor: "pointer",
                }}
              >
                <ArrowBackIcon /> Back
              </TextView>
            </form>
          )}
        </Formik>
      </>
    );
  };

  const renderPasswordForm = () => {
    return (
      <>
        <TextView style={{ textAlign: "center", marginBottom: 20 }}>
          Your new password must be unique from the previously used passwords.
        </TextView>
        <Formik
          validationSchema={passwordSchema}
          initialValues={{ password: "", confirmPassword: "" }}
          onSubmit={handleSubmitPassword}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
          }) => (
            <form noValidate onSubmit={handleSubmit}>
              <Styled.InputWrapper>
                <Input
                  type="password"
                  name="password"
                  variant={"secondary"}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.password}
                  placeholder="New Password"
                  containerStyle={{ marginBottom: 0 }}
                />
                <Styled.Error>
                  {errors.password && touched.password && (
                    <>{errors.password}</>
                  )}
                </Styled.Error>
              </Styled.InputWrapper>
              <Styled.InputWrapper>
                <Input
                  type="password"
                  name="confirmPassword"
                  variant={"secondary"}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.confirmPassword}
                  placeholder="Confirm Password"
                  containerStyle={{ marginBottom: 0 }}
                />
                <Styled.Error>
                  {errors.confirmPassword && touched.confirmPassword && (
                    <>{errors.confirmPassword}</>
                  )}
                </Styled.Error>
              </Styled.InputWrapper>

              <Button
                isLoading={isSubmitting}
                style={{ marginTop: 20, marginBottom: 20 }}
                type="submit"
              >
                Reset Password
              </Button>
              <TextView
                onClick={() => nav("/login")}
                style={{
                  fontWeight: 700,
                  textAlign: "center",
                  marginTop: 10,
                  cursor: "pointer",
                }}
              >
                <ArrowBackIcon /> Back to Login
              </TextView>
            </form>
          )}
        </Formik>
      </>
    );
  };

  return (
    <AuthWrapper title={title}>
      {step === 0 && renderEmailForm()}
      {step === 1 && renderMethodSelection()}
      {step === 2 && renderOtpForm()}
      {step === 4 && renderPasswordForm()}
      <EmailVerificationPopup
        open={showEmailVerificationPopup}
        onClose={() => setShowEmailVerificationPopup(false)}
        email={email}
      />
    </AuthWrapper>
  );
};

export default ForgotPassword;
